import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Dumbbell } from 'lucide-react';

const itemsOf = (menu) => [...(menu?.querySelectorAll('[role="menuitemradio"]') || [])];

/**
 * The library's equipment filter: a pill that opens a menu of the equipment with how many exercises each would show
 * with the other filters on (a zero is dimmed, still pickable). One at a time; the first item takes it off
 */
export const EquipmentMenu = ({ value, options, label, anyLabel, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const current = options.find(({ key }) => key === value);
  const items = [{ key: '', label: anyLabel }, ...options];

  // Open: the checked item takes the focus; a press anywhere else closes it
  useEffect(() => {
    if (!isOpen) return undefined;
    const list = itemsOf(menuRef.current);
    (list.find((item) => item.getAttribute('aria-checked') === 'true') || list[0])?.focus();
    const handlePointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen]);

  const close = () => {
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  const pick = (key) => {
    if (key !== value) onChange(key);
    close();
  };

  const handleKeyDown = (event) => {
    const list = itemsOf(menuRef.current);
    const index = list.indexOf(document.activeElement);
    const move = { ArrowDown: index + 1, ArrowUp: index - 1, Home: 0, End: list.length - 1 }[event.key];
    if (move !== undefined) {
      event.preventDefault();
      list[(move + list.length) % list.length]?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'Tab') {
      setIsOpen(false);
    }
  };

  return (
    <div className="mt-bib-eq" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`mt-bib-eq__btn${value ? ' is-set' : ''}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={current ? `${label}: ${current.label}` : label}
        onClick={() => setIsOpen((open) => !open)}
      >
        <Dumbbell className="mt-bib-eq__icon" aria-hidden="true" />
        <span className="mt-bib-eq__label">{current ? current.label : label}</span>
        <ChevronDown className="mt-bib-eq__chevron" aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="mt-bib-menu" role="menu" aria-label={label} ref={menuRef} onKeyDown={handleKeyDown}>
          {items.map(({ key, label: itemLabel, count }) => (
            <button
              key={key || 'any'}
              type="button"
              role="menuitemradio"
              aria-checked={key === value}
              tabIndex={-1}
              className={`mt-bib-menu__item${count === 0 ? ' is-zero' : ''}${key ? '' : ' mt-bib-menu__item--any'}`}
              onClick={() => pick(key)}
            >
              <span className="mt-bib-menu__check" aria-hidden="true">
                {key === value && <Check />}
              </span>
              <span className="mt-bib-menu__label">{itemLabel}</span>
              {count !== undefined && <span className="mt-bib-menu__count">{count}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
